import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders, formatDate } from "../lib/utils";
import { useApiMessages } from "../hooks/useApiMessages";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Building2, Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export const Companies = () => {
  const { t } = useTranslation();
  const { getErrorMessage, translateMessage } = useApiMessages();
  const { isAdmin, isRevendedor, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    notes: "",
    preferred_language: "",
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/companies`, {
        headers: getAuthHeaders(),
      });
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error(getErrorMessage(error, "api.errors.loadError"));
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [getErrorMessage]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleOpenForm = (company = null) => {
    if (company) {
      setSelectedCompany(company);
      setFormData({
        name: company.name,
        address: company.address || "",
        phone: company.phone || "",
        email: company.email || "",
        notes: company.notes || "",
        preferred_language: company.preferred_language || "",
      });
    } else {
      setSelectedCompany(null);
      setFormData({ name: "", address: "", phone: "", email: "", notes: "", preferred_language: "" });
    }
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCompany) {
        await axios.put(
          `${API_URL}/companies/${selectedCompany.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success(translateMessage("Compañía actualizada"));
      } else {
        await axios.post(`${API_URL}/companies`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success(translateMessage("Compañía creada"));
      }
      setIsFormOpen(false);
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.saveError"));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/companies/${selectedCompany.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(t("api.success.companyDeleted"));
      setIsDeleteOpen(false);
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.deleteError"));
    }
  };

  const filteredCompanies = Array.isArray(companies) ? companies.filter((c) =>
    c?.name?.toLowerCase().includes(search.toLowerCase())
  ) : [];

  return (
    <div className="space-y-6" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("pages.companies.title")}
          </h1>
          <p className="text-slate-600 mt-1">
            {isAdmin() 
              ? t("pages.companies.subtitle")
              : isRevendedor() 
                ? t("pages.companies.subtitleReseller", { used: user?.assigned_company_ids?.length || 0, limit: user?.company_limit || "∞" })
                : t("pages.companies.subtitle")}
          </p>
        </div>
        {(isAdmin() || isRevendedor()) && (
          <Button onClick={() => handleOpenForm()} data-testid="add-company-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t("pages.companies.newCompany")}
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t("pages.companies.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-companies"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pages.companies.name")}</TableHead>
                <TableHead>{t("pages.companies.address")}</TableHead>
                <TableHead>{t("pages.companies.phone")}</TableHead>
                <TableHead>{t("pages.companies.created")}</TableHead>
                <TableHead>{t("pages.companies.status")}</TableHead>
                <TableHead className="text-right">{t("pages.companies.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    {t("pages.companies.noCompanies")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <span className="font-medium">{company.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {company.address || "-"}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {company.phone || "-"}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {formatDate(company.created_at)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          company.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {company.is_active ? t("pages.companies.active") : t("pages.companies.inactive")}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenForm(company)}
                          data-testid={`edit-company-${company.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedCompany(company);
                              setIsDeleteOpen(true);
                            }}
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedCompany ? t("pages.companies.editCompany") : t("pages.companies.newCompanyTitle")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("pages.companies.nameRequired")}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">{t("pages.companies.address")}</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                data-testid="company-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t("pages.companies.phone")}</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                data-testid="company-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("pages.companies.email")}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder={t("pages.companies.emailPlaceholder")}
                data-testid="company-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="preferred_language">{t("pages.companies.preferredLanguage")}</Label>
              <Select
                value={formData.preferred_language || ""}
                onValueChange={(value) =>
                  setFormData({ ...formData, preferred_language: value || null })
                }
              >
                <SelectTrigger data-testid="company-language-select">
                  <SelectValue placeholder={t("pages.companies.selectLanguage")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t("pages.companies.defaultLanguage")}</SelectItem>
                  <SelectItem value="es">🇪🇸 {t("pages.companies.spanish")}</SelectItem>
                  <SelectItem value="en">🇬🇧 {t("pages.companies.english")}</SelectItem>
                  <SelectItem value="fr">🇫🇷 {t("pages.companies.french")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">{t("pages.companies.notes")}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder={t("pages.companies.notesPlaceholder")}
                rows={4}
                data-testid="company-notes-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsFormOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" data-testid="save-company-btn">
                {selectedCompany ? t("pages.companies.saveChanges") : t("pages.companies.createCompany")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.companies.deleteCompany")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.companies.deleteConfirm", { name: selectedCompany?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-company"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
